import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type ButtonVariant = "primary" | "secondary" | "tertiary" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  /** Takes full width of its container — useful for forms. */
  fullWidth?: boolean;
  /** Icon rendered on the leading edge. */
  leadingIcon?: ReactNode;
  /** Icon rendered on the trailing edge. */
  trailingIcon?: ReactNode;
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-brand-500 text-white shadow-sm hover:bg-brand-600 active:bg-brand-700 focus-visible:shadow-focus disabled:bg-brand-200 disabled:text-white/80",
  secondary:
    "border border-border bg-surface-card text-ink hover:bg-surface-subtle active:bg-surface-muted focus-visible:shadow-focus disabled:text-ink-subtle disabled:bg-surface-subtle",
  tertiary:
    "text-ink hover:bg-surface-subtle active:bg-surface-muted focus-visible:shadow-focus disabled:text-ink-subtle",
  danger:
    "bg-danger-500 text-white shadow-sm hover:bg-danger-600 active:bg-danger-600 focus-visible:shadow-focus-danger disabled:bg-danger-500/60",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-8 px-4 text-caption",
  md: "h-10 px-6 text-body",
  lg: "h-12 px-8 text-body",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      isLoading = false,
      fullWidth = false,
      leadingIcon,
      trailingIcon,
      disabled,
      children,
      className,
      type = "button",
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled ?? isLoading;
    return (
      <button
        {...rest}
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={isLoading || undefined}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-pill font-medium transition-colors duration-150 ease-apple-smooth",
          "focus:outline-none disabled:cursor-not-allowed",
          variantClasses[variant],
          sizeClasses[size],
          fullWidth && "w-full",
          className,
        )}
      >
        {isLoading ? <Spinner /> : leadingIcon}
        <span>{children}</span>
        {!isLoading && trailingIcon ? trailingIcon : null}
      </button>
    );
  },
);
Button.displayName = "Button";

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
